import React, { useState } from "react";
import "./LoginSignup.css";
import { useLocation, useNavigate } from "react-router-dom";
import TermsAndConditions from '../../pages/TermsAndConditions';
import { sendSMSOtp } from "../../utils/smsOtp";
import {
  getPendingAuthRedirect,
  savePendingAuthRedirect,
} from "../../utils/authRedirect";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const normalizePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");

    if (digits.length === 10) {
      return digits;
    }

    if (digits.length === 12 && digits.startsWith("91")) {
      return digits.slice(2);
    }

    throw new Error("Please enter a valid 10-digit phone number.");
  };

  const clearPendingAuth = () => {
    localStorage.removeItem("pendingEmail");
    localStorage.removeItem("pendingPhone");
    localStorage.removeItem("pendingOtpPurpose");
    localStorage.removeItem("pendingOtpMethod");
    localStorage.removeItem("pendingRegistrationData");
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const { name, email, phone, password } = formData;
    if (!name || !email || !phone || !password) {
      return setError("All fields are required.");
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions.');
      return;
    }

    // Start loading state
    setIsSubmitting(true);
    setError("");
    setLoadingMessage("Verifying your phone and preparing account setup...");

    try {
      // Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));

      const redirectPath = location.state?.from || getPendingAuthRedirect();
      clearPendingAuth();

      if (redirectPath) {
        savePendingAuthRedirect(redirectPath);
      }

      const normalizedPhone = normalizePhone(formData.phone);
      await sendSMSOtp(normalizedPhone, "register");

      localStorage.setItem("pendingPhone", normalizedPhone);
      localStorage.setItem("pendingOtpPurpose", "register");
      localStorage.setItem("pendingOtpMethod", "sms");
      localStorage.setItem(
        "pendingRegistrationData",
        JSON.stringify({
          ...formData,
          email: formData.email.trim().toLowerCase(),
          phone: normalizedPhone,
        })
      );
      
      // Add small delay before navigation for smooth transition
      setTimeout(() => {
        navigate("/verify-otp");
      }, 500);
      
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Registration failed");
    } finally {
      // Stop loading state
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  const openTerms = () => {
    setShowTerms(true);
  };
  const handleDone = () => {
    setShowTerms(false);
    setAcceptedTerms(true);
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2 className="auth-title">Create Account</h2>
        <form onSubmit={handleRegister}>
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (10 digits)"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Create Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <p className="terms-instruction">
            Please read the Terms and Conditions before registering.
          </p>
          <div className="terms-row">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              required
            />
            <label htmlFor="terms">
              I accept the{' '}
              <span
                className="terms-link"
                onClick={e => { e.preventDefault(); openTerms(); }}
              >
                Terms and Conditions
              </span>
            </label>
          </div>

          {error && <p className="auth-error">{error}</p>}
          <button 
            type="submit" 
            className={`auth-button ${isSubmitting ? 'auth-button-loading' : ''}`}
            disabled={isSubmitting || !acceptedTerms}
          >
            {isSubmitting ? (
              <span className="button-content">
                <span className="spinner"></span>
                Creating Account...
              </span>
            ) : (
              "Send SMS OTP"
            )}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            Already have an account?{" "}
            <span
              onClick={() => navigate("/login", {
                state: { from: location.state?.from || getPendingAuthRedirect() },
              })}
            >
              Login
            </span>
          </p>
        </div>
      </div>
      
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">{loadingMessage}</p>
          </div>
        </div>
      )}
      
      {showTerms && (
        <div className="terms-modal-overlay">
          <div className="terms-modal-shell">
            <button
              type="button"
              className="terms-modal-close"
              aria-label="Close terms and conditions"
              onClick={() => setShowTerms(false)}
            >
              &times;
            </button>
            <TermsAndConditions onDone={handleDone} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
